import type { MouseEvent } from "react";
import type { NavigateFunction } from "react-router-dom";

import { Avatar, Box, Divider, Grid, Icon } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useContext, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { ApplicationEnum } from "~/@types/enums";
import { ROLES } from "~/@types/roles";
import { AuthContext } from "~/auth/AuthProvider";
import { BodyTextSmall } from "~/components/Typographies";
import ProductName from "~/components/worksuite/ProductName";
import useModuleEnabledMap from "~/hooks/useModuleEnabledMap";
import runGuardedExit, { hasUnsavedChanges } from "~/layout/unsavedChangesRegistry";
import useModuleBranding from "~/layout/useModuleBranding";
import buildConfinedAppSwitchHref from "~/pwa/confinedAppSwitchHref";
import { isAndroid } from "~/pwa/openApp";
import isAppSurfaced from "~/pwa/optInApps";
import useStandaloneApp from "~/pwa/useStandaloneApp";
import moduleColors, { productModules } from "~/themes/moduleColors";

interface Application {
	active?: boolean;
	color?: string;
	key: ApplicationEnum;
	link: string;
	logo: string;
	mobile?: boolean;
	name: string;
}

const applications: Application[] = [
	{
		active: false,
		key: ApplicationEnum.WORKSUITE,
		link: "/",
		logo: "/logos/company.svg",
		name: "WORK SUITE"
	},
	{
		active: true,
		color: moduleColors.FORECAST,
		key: ApplicationEnum.FORECAST,
		link: "/forecast",
		logo: "/logos/forecast.svg",
		name: "FORECAST"
	},
	{
		active: true,
		color: moduleColors.FULFILL,
		key: ApplicationEnum.FULFILL,
		link: "/fulfill",
		logo: "/logos/fulfill.svg",
		name: "FULFILL"
	},
	{
		active: true,
		color: moduleColors.PRODUCE,
		key: ApplicationEnum.PRODUCE,
		link: "/produce",
		logo: "/logos/produce.svg",
		name: "PRODUCE"
	},
	{
		active: true,
		color: moduleColors.RESTOCK,
		key: ApplicationEnum.RESTOCK,
		link: "/restock",
		logo: "/logos/restock.svg",
		name: "RESTOCK"
	},
	{
		active: true,
		color: moduleColors.PORTAL,
		key: ApplicationEnum.PORTAL,
		link: "/portal",
		logo: "/logos/portal.svg",
		name: "PORTAL"
	},
	{
		active: true,
		color: moduleColors.REPORTS,
		key: ApplicationEnum.REPORTS,
		link: "/reports",
		logo: "/logos/report.svg",
		name: "REPORTS"
	},
	{
		active: true,
		color: moduleColors.AVAILABILITY,
		key: ApplicationEnum.AVAILABILITY,
		link: "/availability",
		logo: "aod",
		mobile: true,
		name: "AVAILABILITY"
	},
	{
		active: true,
		color: moduleColors.MATERIAL,
		key: ApplicationEnum.MATERIAL,
		link: "/material",
		logo: "inventory_2",
		mobile: true,
		name: "MATERIAL"
	},
	{
		active: true,
		color: moduleColors.ENGAGE,
		key: ApplicationEnum.ENGAGE,
		link: "/engage",
		logo: "storefront",
		mobile: true,
		name: "ENGAGE"
	},
	{
		active: true,
		color: moduleColors.VERIFY,
		key: ApplicationEnum.VERIFY,
		link: "/verify",
		logo: "verified",
		mobile: true,
		name: "VERIFY"
	},
	{
		active: true,
		color: moduleColors.EVENTS,
		key: ApplicationEnum.EVENTS,
		link: "/events",
		logo: "event_note",
		mobile: true,
		name: "EVENTS"
	},
	{
		active: true,
		color: moduleColors.COUNT,
		key: ApplicationEnum.COUNT,
		link: "/count",
		logo: "qr_code_scanner",
		mobile: true,
		name: "COUNT"
	},
	{
		active: true,
		color: moduleColors.INTELLIGENCE,
		key: ApplicationEnum.INTELLIGENCE,
		link: "/intelligence",
		logo: "insights",
		name: "INTELLIGENCE"
	},
	{
		active: true,
		color: moduleColors.BENCHMARK,
		key: ApplicationEnum.BENCHMARK,
		link: "/benchmark",
		logo: "leaderboard",
		name: "BENCHMARK"
	}
];

interface AvatarButtonProps {
	app: Application;
	onClick: () => void;
}

interface ConfinedLinkMode {
	android: boolean;
	confined: boolean;
}

interface ApplicationLinksProps {
	filteredApplications: Application[];
	homeApp: Application;
	mode: ConfinedLinkMode;
	navigate: NavigateFunction;
	onClose: () => void;
}

const guardAppExit = (proceed: () => void) => (event: MouseEvent<HTMLElement>) => {
	if (!hasUnsavedChanges()) return;
	event.preventDefault();
	runGuardedExit(proceed);
};

const AvatarButton = ({ app, onClick }: AvatarButtonProps) => {
	const accent = app.color || moduleColors.WORKSUITE;

	if (app.mobile) {
		return (
			<Box
				onClick={onClick}
				sx={{
					"&:hover": { transform: "translateY(-2px)" },
					alignItems: "center",
					cursor: "pointer",
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					p: 0.5,
					textAlign: "center",
					transition: "transform 0.2s ease"
				}}
			>
				<Box
					sx={{
						alignItems: "center",
						backgroundColor: alpha(accent, 0.07),
						border: "2px solid",
						borderColor: accent,
						borderRadius: "10px",
						display: "flex",
						flexDirection: "column",
						height: 44,
						justifyContent: "center",
						position: "relative",
						width: 30
					}}
				>
					<Box
						sx={{
							backgroundColor: accent,
							borderRadius: "2px",
							height: 2,
							left: "50%",
							position: "absolute",
							top: 4,
							transform: "translateX(-50%)",
							width: 10
						}}
					/>
					<Icon sx={{ color: accent, fontSize: "1rem" }}>{app.logo}</Icon>
					<Box
						sx={{
							backgroundColor: accent,
							borderRadius: "2px",
							bottom: 3,
							height: 2,
							left: "50%",
							opacity: 0.4,
							position: "absolute",
							transform: "translateX(-50%)",
							width: 11
						}}
					/>
				</Box>
				<BodyTextSmall bold sx={{ fontSize: "0.64rem" }}>
					{app.name}
				</BodyTextSmall>
			</Box>
		);
	}

	const isProduct = productModules.includes(app.name);
	const leading = app.logo.startsWith("/") ? (
		<Avatar
			alt={app.name}
			src={app.logo}
			sx={{ borderRadius: 1.5, flexShrink: 0, height: 28, width: 28 }}
			variant="square"
		/>
	) : (
		<Box
			sx={{
				alignItems: "center",
				backgroundColor: accent,
				borderRadius: 1.5,
				display: "flex",
				flexShrink: 0,
				height: 28,
				justifyContent: "center",
				width: 28
			}}
		>
			<Icon sx={{ color: "common.white", fontSize: "1.05rem" }}>{app.logo}</Icon>
		</Box>
	);

	return (
		<Box
			onClick={onClick}
			sx={{
				"&:hover": {
					"&::before": { opacity: 1 },
					borderColor: "transparent",
					boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.common.black, 0.1)}`,
					transform: "translateY(-2px)"
				},
				"&::before": {
					backgroundColor: accent,
					borderRadius: "3px 0 0 3px",
					bottom: 0,
					content: '""',
					left: 0,
					opacity: 0,
					position: "absolute",
					top: 0,
					transition: "opacity 0.2s ease",
					width: 3
				},
				alignItems: "center",
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 2,
				boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.common.black, 0.05)}`,
				cursor: "pointer",
				display: "flex",
				gap: 1,
				overflow: "hidden",
				position: "relative",
				px: 1.25,
				py: 0.75,
				transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease"
			}}
		>
			{leading}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				{isProduct ? <ProductName fontSize="0.8rem" name={app.name} /> : <BodyTextSmall bold>{app.name}</BodyTextSmall>}
			</Box>
		</Box>
	);
};

const tileLinkSx = { color: "inherit", textDecoration: "none" } as const;

const renderTile = (app: Application, onClose: () => void, mode: ConfinedLinkMode, navigate: NavigateFunction) => {
	if (mode.confined) {
		const href = buildConfinedAppSwitchHref(app.link, mode.android, window.location);
		if (mode.android) {
			return (
				<Box component="a" href={href} onClick={guardAppExit(() => window.location.assign(href))} sx={tileLinkSx}>
					<AvatarButton app={app} onClick={onClose} />
				</Box>
			);
		}
		return (
			<Box component="a" href={href} rel="noopener" sx={tileLinkSx} target="_blank">
				<AvatarButton app={app} onClick={onClose} />
			</Box>
		);
	}
	return (
		<Box component={RouterLink} onClick={guardAppExit(() => navigate(app.link))} sx={tileLinkSx} to={app.link}>
			<AvatarButton app={app} onClick={onClose} />
		</Box>
	);
};

const menuSectionLabelSx = {
	color: "text.secondary",
	fontSize: "0.68rem",
	letterSpacing: "0.12em",
	textTransform: "uppercase"
} as const;

const homeLinkSx = {
	"&:hover": { borderColor: "primary.main" },
	alignItems: "center",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: 2,
	color: "text.primary",
	display: "flex",
	gap: 1,
	mb: 1.5,
	px: 1.25,
	py: 0.625,
	textDecoration: "none"
} as const;

const HomeLinkContent = ({ homeApp }: { homeApp: Application }) => (
	<>
		<Icon sx={{ color: "primary.main", fontSize: "1.1rem" }}>{homeApp.logo}</Icon>
		<BodyTextSmall bold>{homeApp.name}</BodyTextSmall>
		<Icon sx={{ color: "text.disabled", fontSize: "1rem", ml: "auto" }}>chevron_right</Icon>
	</>
);

const ApplicationLinks = ({ filteredApplications, homeApp, mode, navigate, onClose }: ApplicationLinksProps) => (
	<Box sx={{ fontFamily: "'Montserrat', sans-serif" }}>
		{!mode.confined && (
			<>
				<Box
					component={RouterLink}
					onClick={(event: MouseEvent<HTMLElement>) => {
						onClose();
						guardAppExit(() => navigate(homeApp.link))(event);
					}}
					sx={homeLinkSx}
					to={homeApp.link}
				>
					<HomeLinkContent homeApp={homeApp} />
				</Box>
				<BodyTextSmall bold sx={menuSectionLabelSx}>
					WORK SUITE
				</BodyTextSmall>
				<Grid container spacing={1} sx={{ justifyContent: "center", py: 1, width: "100%" }}>
					{filteredApplications
						.filter((app) => app.active && !app.mobile)
						.map((app) => (
							<Grid key={app.key} size={6}>
								{renderTile(app, onClose, mode, navigate)}
							</Grid>
						))}
				</Grid>
				<Divider sx={{ mb: 1.5 }} />
			</>
		)}
		<BodyTextSmall bold sx={menuSectionLabelSx}>
			MOBILE APPS
		</BodyTextSmall>
		<Grid container spacing={0.75} sx={{ py: 1, width: "100%" }}>
			{filteredApplications
				.filter((app) => app.active && app.mobile)
				.map((app) => (
					<Grid key={app.key} size={4}>
						{renderTile(app, onClose, mode, navigate)}
					</Grid>
				))}
		</Grid>
	</Box>
);

const ApplicationsMenu = () => {
	const { currentUser } = useContext(AuthContext);
	const navigate = useNavigate();
	const { isConfined } = useStandaloneApp();
	const isAdmin = currentUser?.roles?.includes(ROLES.SUPER_ADMIN) || false;
	const flagMap = useModuleEnabledMap();
	const benchmarkBranding = useModuleBranding(ApplicationEnum.BENCHMARK);

	const filteredApplications = useMemo(
		() =>
			applications
				.filter((app) => {
					if (app.key === ApplicationEnum.INTELLIGENCE && !isAdmin) {
						return false;
					}
					return isAppSurfaced(app.key, flagMap) && flagMap[app.key] !== false;
				})
				.map((app) => (app.key === ApplicationEnum.BENCHMARK ? { ...app, name: benchmarkBranding.displayName } : app)),
		[benchmarkBranding.displayName, flagMap, isAdmin]
	);

	const homeApp: Application = {
		active: true,
		color: "",
		key: ApplicationEnum.WORKSUITE,
		link: "/",
		logo: "home",
		mobile: false,
		name: "HOME"
	};

	return (
		<ApplicationLinks
			filteredApplications={filteredApplications}
			homeApp={homeApp}
			mode={{ android: isAndroid(), confined: isConfined }}
			navigate={navigate}
			onClose={() => {}}
		/>
	);
};

export default ApplicationsMenu;
